/**
 * Launch geometry resolver — pure calculations for projectile spawn and trajectory.
 *
 * Implements canonical spawn point and initial unit direction resolution across
 * client local prediction, server launch admission, and remote observer presentation.
 *
 * Muzzle: facing right the projectile's top-left is the caster's top-left +40 in x,
 * -5 in y. Facing left it is the mirror image about the caster's centre, so the
 * projectile's centre starts the same distance ahead either way (see muzzleSpawnX).
 * Normalizes direction vector from the ACTUAL PROJECTILE CENTER, not muzzle top-left.
 *
 * Pure module with zero engine/browser/server dependencies.
 */

import { PLAYER_HITBOX } from '../logic/CombatAnchors';
import {
  DEFAULT_MUZZLE_OFFSET_X,
  DEFAULT_MUZZLE_OFFSET_Y,
  VECTOR_TOLERANCE,
  type AssistedLaunchInput,
  type AssistedLaunchResult,
  type Facing,
  type Point,
} from './contract';

/** The caster width launch geometry measures from: a finite positive width, else the player hitbox. */
export function launchCasterWidth(width: number | undefined): number {
  return typeof width === 'number' && Number.isFinite(width) && width > 0 ? width : PLAYER_HITBOX.width;
}

/**
 * Projectile top-left x at launch. Facing right it is `casterX + offsetX`, which
 * puts the projectile centre `d = offsetX + projectileWidth/2 - casterWidth/2`
 * ahead of the caster centre. Facing left the centre sits `d` behind the caster
 * centre instead: `casterX + casterWidth - offsetX - projectileWidth`.
 */
export function muzzleSpawnX(
  casterX: number,
  casterWidth: number,
  direction: Facing,
  projectileWidth: number,
  offsetX: number = DEFAULT_MUZZLE_OFFSET_X,
): number {
  return direction === 'right'
    ? casterX + offsetX
    : casterX + casterWidth - offsetX - projectileWidth;
}

/**
 * Compute the top-left spawn coordinates for a projectile given caster position,
 * direction, projectile width and caster width (player hitbox when omitted).
 */
export function computeMuzzlePosition(
  casterPosition: Point,
  direction: Facing,
  projectileWidth: number,
  casterWidth: number = PLAYER_HITBOX.width,
): Point {
  return Object.freeze({
    x: muzzleSpawnX(casterPosition.x, casterWidth, direction, projectileWidth),
    y: casterPosition.y + DEFAULT_MUZZLE_OFFSET_Y,
  });
}

/**
 * Compute the center point of a spawned projectile.
 */
export function computeProjectileCenter(
  spawnPosition: Point,
  dimensions: { width: number; height: number },
): Point {
  return Object.freeze({
    x: spawnPosition.x + dimensions.width / 2,
    y: spawnPosition.y + dimensions.height / 2,
  });
}

/**
 * Resolve the complete launch plan for an assisted projectile ability.
 *
 * Guarantees:
 * - Targeted intent missing its target returns an explicit failure, never falling back to facing.
 * - Directional intent with missing, non-finite, or coincident pointer falls back to horizontal facing.
 * - Facing shots are strictly horizontal unit vectors `(1, 0)` or `(-1, 0)`.
 * - Free aim and directional fallback output `targetId: null` and `guidance: 'straight'`.
 * - Targeted shots retain `targetId`; explicitly authored straight shots disable guidance.
 * - Normalization occurs from actual projectile center `(spawnX + w/2, spawnY + h/2)`.
 * - Returned objects are deeply frozen and immutable.
 */
export function resolveAssistedLaunch(input: AssistedLaunchInput): AssistedLaunchResult {
  // 1. Validate profile
  if (
    !input ||
    !input.profile ||
    typeof input.profile.speed !== 'number' ||
    !Number.isFinite(input.profile.speed) ||
    input.profile.speed <= 0 ||
    typeof input.profile.width !== 'number' ||
    !Number.isFinite(input.profile.width) ||
    input.profile.width <= 0 ||
    typeof input.profile.height !== 'number' ||
    !Number.isFinite(input.profile.height) ||
    input.profile.height <= 0
  ) {
    return Object.freeze({ ok: false, reason: 'invalid_profile' });
  }

  // 2. Validate caster
  if (
    !input.caster ||
    !input.caster.position ||
    typeof input.caster.position.x !== 'number' ||
    !Number.isFinite(input.caster.position.x) ||
    typeof input.caster.position.y !== 'number' ||
    !Number.isFinite(input.caster.position.y)
  ) {
    return Object.freeze({ ok: false, reason: 'invalid_caster' });
  }

  const casterFacing: Facing = input.caster.facing === 'left' ? 'left' : 'right';
  const casterWidth = launchCasterWidth(input.caster.width);
  const casterCenterX = input.caster.position.x + casterWidth / 2;

  // 3. Validate intent
  if (!input.intent || input.intent.version !== 1) {
    return Object.freeze({ ok: false, reason: 'invalid_intent' });
  }

  const mode = input.intent.mode;
  if (input.profile.forceStraight && mode === 'assisted_auto') {
    return Object.freeze({ ok: false, reason: 'invalid_intent' });
  }

  // ── Targeted Branch (manual_locked / assisted_auto) ─────────────────────────
  if (mode === 'manual_locked' || mode === 'assisted_auto') {
    if (
      !input.targetPoint ||
      typeof input.targetPoint.x !== 'number' ||
      !Number.isFinite(input.targetPoint.x) ||
      typeof input.targetPoint.y !== 'number' ||
      !Number.isFinite(input.targetPoint.y)
    ) {
      // Targeted intent missing its matching target fails rather than retargeting/facing fallback
      return Object.freeze({ ok: false, reason: 'missing_target' });
    }

    // Direction chosen relative to caster combat center; facing at a tie
    let direction: Facing;
    if (input.targetPoint.x > casterCenterX) {
      direction = 'right';
    } else if (input.targetPoint.x < casterCenterX) {
      direction = 'left';
    } else {
      direction = casterFacing;
    }

    const muzzle = computeMuzzlePosition(input.caster.position, direction, input.profile.width, casterWidth);
    const projCenter = computeProjectileCenter(muzzle, input.profile);

    const dx = input.targetPoint.x - projCenter.x;
    const dy = input.targetPoint.y - projCenter.y;
    const len = Math.hypot(dx, dy);

    if (!Number.isFinite(len)) {
      return Object.freeze({ ok: false, reason: 'invalid_vector' });
    }

    let unitX: number;
    let unitY: number;

    if (len < 0.0001) {
      // Target point coincident with projectile center
      unitX = direction === 'right' ? 1 : -1;
      unitY = 0;
    } else {
      unitX = dx / len;
      unitY = dy / len;
    }

    return Object.freeze({
      ok: true,
      spawnX: muzzle.x,
      spawnY: muzzle.y,
      direction,
      unitX,
      unitY,
      targetId: input.intent.targetId,
      guidance: input.profile.forceStraight || input.profile.homingStrength <= 0 ? 'straight' : 'authored',
    });
  }

  // ── Directional Branch (free_aim / directional_fallback) ────────────────────
  if (mode === 'free_aim' || mode === 'directional_fallback') {
    // Subcase: Validated server vector input { kind: 'vector', unit, muzzleFacing }
    if (input.directional && input.directional.kind === 'vector') {
      const { unit, muzzleFacing } = input.directional;
      if (
        !unit ||
        typeof unit.x !== 'number' ||
        !Number.isFinite(unit.x) ||
        typeof unit.y !== 'number' ||
        !Number.isFinite(unit.y) ||
        (muzzleFacing !== 'left' && muzzleFacing !== 'right')
      ) {
        return Object.freeze({ ok: false, reason: 'invalid_vector' });
      }

      const len = Math.hypot(unit.x, unit.y);
      if (len === 0 || !Number.isFinite(len) || Math.abs(len - 1) > VECTOR_TOLERANCE) {
        return Object.freeze({ ok: false, reason: 'invalid_vector' });
      }

      if (input.intent.source === 'facing') {
        if (muzzleFacing !== casterFacing) {
          return Object.freeze({ ok: false, reason: 'invalid_vector' });
        }
        const expectedUnitX = casterFacing === 'right' ? 1 : -1;
        if (Math.abs(unit.x - expectedUnitX) > VECTOR_TOLERANCE || unit.y !== 0) {
          return Object.freeze({ ok: false, reason: 'invalid_vector' });
        }
      }

      const muzzle = computeMuzzlePosition(input.caster.position, muzzleFacing, input.profile.width, casterWidth);
      return Object.freeze({
        ok: true,
        spawnX: muzzle.x,
        spawnY: muzzle.y,
        direction: muzzleFacing,
        unitX: unit.x / len,
        unitY: unit.y / len,
        targetId: null,
        guidance: 'straight',
      });
    }

    // Subcase: Intent source is explicitly 'facing'
    if (input.intent.source === 'facing') {
      const direction = casterFacing;
      const muzzle = computeMuzzlePosition(input.caster.position, direction, input.profile.width, casterWidth);
      return Object.freeze({
        ok: true,
        spawnX: muzzle.x,
        spawnY: muzzle.y,
        direction,
        unitX: direction === 'right' ? 1 : -1,
        unitY: 0,
        targetId: null,
        guidance: 'straight',
      });
    }

    // Subcase: Intent source is 'pointer' with optional local point
    const pointer = input.directional?.kind === 'point' ? input.directional.point : undefined;

    if (
      !pointer ||
      typeof pointer.x !== 'number' ||
      !Number.isFinite(pointer.x) ||
      typeof pointer.y !== 'number' ||
      !Number.isFinite(pointer.y)
    ) {
      // Missing or non-finite pointer falls back to horizontal facing
      const direction = casterFacing;
      const muzzle = computeMuzzlePosition(input.caster.position, direction, input.profile.width, casterWidth);
      return Object.freeze({
        ok: true,
        spawnX: muzzle.x,
        spawnY: muzzle.y,
        direction,
        unitX: direction === 'right' ? 1 : -1,
        unitY: 0,
        targetId: null,
        guidance: 'straight',
      });
    }

    // Direction chosen relative to caster combat center; facing at a tie
    let direction: Facing;
    if (pointer.x > casterCenterX) {
      direction = 'right';
    } else if (pointer.x < casterCenterX) {
      direction = 'left';
    } else {
      direction = casterFacing;
    }

    const muzzle = computeMuzzlePosition(input.caster.position, direction, input.profile.width, casterWidth);
    const projCenter = computeProjectileCenter(muzzle, input.profile);

    const dx = pointer.x - projCenter.x;
    const dy = pointer.y - projCenter.y;
    const len = Math.hypot(dx, dy);

    if (!Number.isFinite(len) || len < 0.0001) {
      // An unusable relative pointer direction falls back to facing.
      const fallbackMuzzle = computeMuzzlePosition(input.caster.position, casterFacing, input.profile.width, casterWidth);
      return Object.freeze({
        ok: true,
        spawnX: fallbackMuzzle.x,
        spawnY: fallbackMuzzle.y,
        direction: casterFacing,
        unitX: casterFacing === 'right' ? 1 : -1,
        unitY: 0,
        targetId: null,
        guidance: 'straight',
      });
    }

    return Object.freeze({
      ok: true,
      spawnX: muzzle.x,
      spawnY: muzzle.y,
      direction,
      unitX: dx / len,
      unitY: dy / len,
      targetId: null,
      guidance: 'straight',
    });
  }

  return Object.freeze({ ok: false, reason: 'invalid_intent' });
}
